import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CalendarRange,
  ChevronDown,
  Menu,
  MessageSquare,
  Moon,
  Plus,
  Search,
  Sun,
} from "lucide-react";
import { Button, Dropdown, DropdownItem, Select } from "@/components/ui";
import { useAppStore } from "@/store/appStore";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell, NotificationPanel } from "./NotificationCenter";
import { ProfileMenu } from "./ProfileMenu";
import { ConnectivityStatus } from "@/components/status/ConnectivityStatus";

export function Navbar({
  onMenuClick,
  title = "CEO Dashboard",
  subtitle,
}: {
  onMenuClick: () => void;
  title?: string;
  subtitle?: string;
}) {
  const user = useAppStore((s) => s.user);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const dateRange = useAppStore((s) => s.dateRange);
  const setDateRange = useAppStore((s) => s.setDateRange);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setSearchOpen(false);
    setNotifyOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const greeting =
    subtitle ?? (user ? `Welcome back, ${user.name} 👋` : "Welcome back 👋");

  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

  return (
    <>
      <header className="navbar app-shell__navbar">
        <div className="navbar__left">
          <Button
            className="btn--menu navbar__icon-btn"
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            onClick={onMenuClick}
          >
            <Menu size={18} />
          </Button>
          <div className="navbar__titles">
            <h1 className="navbar__title">{title}</h1>
            <p className="navbar__subtitle">{greeting}</p>
          </div>
        </div>

        <div className="navbar__actions">
          <div className="navbar__group navbar__group--search">
            <Button
              className="navbar__search-trigger"
              variant="default"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={16} strokeWidth={2} aria-hidden />
              <span className="navbar__search-placeholder">Search anything...</span>
              <kbd className="navbar__search-kbd">{isMac ? "⌘K" : "Ctrl K"}</kbd>
            </Button>

            <Button
              className="navbar__search-icon navbar__icon-btn"
              variant="ghost"
              size="icon"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={18} />
            </Button>
          </div>

          <div className="navbar__group navbar__group--date">
            <label className="sr-only" htmlFor="date-range">
              Date range
            </label>
            <div className="navbar__date">
              <CalendarRange size={16} aria-hidden />
              <Select
                id="date-range"
                value={dateRange.label}
                aria-label="Date range selector"
                onChange={(e) => {
                  const label = e.target.value;
                  if (label.includes("Last 30")) {
                    setDateRange({
                      from: "2026-06-19",
                      to: "2026-07-19",
                      label: "Last 30 days",
                    });
                  } else if (label.includes("Quarter")) {
                    setDateRange({
                      from: "2026-04-01",
                      to: "2026-07-19",
                      label: "This quarter",
                    });
                  } else {
                    setDateRange({
                      from: "2026-07-01",
                      to: "2026-07-19",
                      label: "Jul 1 – Jul 19, 2026",
                    });
                  }
                }}
              >
                <option>Jul 1 – Jul 19, 2026</option>
                <option>Last 30 days</option>
                <option>This quarter</option>
              </Select>
              <ChevronDown size={14} className="navbar__date-caret" aria-hidden />
            </div>
          </div>

          <div className="navbar__group">
            <Dropdown
              trigger={
                <Button
                  variant="primary"
                  size="icon"
                  aria-label="Quick create"
                  className="navbar__create"
                >
                  <Plus size={20} strokeWidth={2.25} />
                </Button>
              }
            >
              <DropdownItem>New lead</DropdownItem>
              <DropdownItem>New meeting</DropdownItem>
              <DropdownItem>New invoice</DropdownItem>
              <DropdownItem>New CMS draft</DropdownItem>
            </Dropdown>
          </div>

          <div className="navbar__group navbar__group--icons">
            <NotificationBell
              onOpen={() => setNotifyOpen(true)}
              className="navbar__icon-btn"
            />

            <Button
              variant="ghost"
              size="icon"
              className="navbar__icon-btn navbar__icon-btn--messages"
              aria-label="Messages"
              title="Messages (soon)"
            >
              <MessageSquare size={18} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="navbar__icon-btn"
              aria-label="Toggle theme"
              title="Theme toggle"
              onClick={toggleTheme}
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </Button>
          </div>

          <div className="navbar__group navbar__group--profile">
            <ConnectivityStatus className="navbar__connectivity" />
            <ProfileMenu />
          </div>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <NotificationPanel open={notifyOpen} onClose={() => setNotifyOpen(false)} />
    </>
  );
}
